import { useEffect, useState, useCallback } from 'react';
import SidebarNav from '../components/MasterSideBar';
import TopbarNav from '../components/TopbarNav';
import ContractsTable from '../components/ContractsTable';
import { getStaff } from '../api/staff-calls-api';
import UploadDocumentModal from '../components/UploadDocumentModal';
import ViewContractModal from '../components/ViewContractModal';
import { toast } from 'react-toastify';

// Returns 'Expired', 'Expiring Soon' (within 60 days), or 'Active'
const getContractStatus = (endDate) => {
  if (!endDate || endDate === 'N/A') return 'N/A';
  const today = new Date();
  const expiry = new Date(endDate);
  if (isNaN(expiry)) return 'N/A';
  if (expiry < today) return 'Expired';
  const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysUntilExpiry <= 60) return 'Expiring Soon';
  return 'Active';
};

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sidebar state for responsiveness
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Upload modal states
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  // View modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const token = localStorage.getItem("token");
  const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
  const staffId = decoded ? decoded.id : null;

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const handleUploadClick = useCallback((contract) => {
    console.log('Upload clicked for contract:', contract);
    setSelectedStaffId(contract.id);
    setIsUploadModalOpen(true);
  }, []);

  const handleViewClick = useCallback((contract) => {
    console.log('View clicked for:', contract);
    setSelectedContract(contract);
    setIsViewModalOpen(true);
  }, []);

  const handleUploadSuccess = useCallback((files) => {
    const staffMember = contracts.find(c => c.id === selectedStaffId);
    setIsUploadModalOpen(false);
    toast.success(`${files.length} contract document(s) uploaded successfully for ${staffMember?.name}!`);
  }, [selectedStaffId, contracts]);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const { list: staffList } = await getStaff();

        const formattedContracts = staffList.map((staff) => {
          const endDate = staff.contractEndDate?.split("T")[0] || 'N/A';
          return {
            id: staff._id || staff.id,
            name: `${staff.firstName} ${staff.lastName}`,
            title: staff.jobTitle || 'N/A',
            type: staff.contractType || 'N/A',
            department: staff.department || 'N/A',
            contractStartDate: staff.contractStartDate?.split("T")[0] || 'N/A',
            contractEndDate: endDate,
            contractDuration: staff.contractDuration || 'N/A',
            referenceNumber: staff.referenceNumber || '',
            status: getContractStatus(endDate),
          };
        });

        setContracts(formattedContracts);
      } catch (err) {
        console.error("❌ Error loading staff contracts:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, []);

  // Stat counts
  const expiredCount = contracts.filter(c => c.status === 'Expired').length;
  const activeCount = contracts.filter(c => c.status === 'Active').length;
  const expiringSoonCount = contracts.filter(c => c.status === 'Expiring Soon').length;

  return (
    <>
      {/* Main Layout */}
      <div className="flex h-screen bg-gray-100">
        {/* Sidebar */}
        <SidebarNav
          visible={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onLogout={handleLogout}
        />

        {/* Main Content Area — md:ml-64 clears the sidebar, pt-16 clears the fixed topbar */}
        <div className="flex flex-col flex-1 md:ml-64 pt-16">
          {/* TopBar */}
          <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

          {/* Page Content */}
          <main className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6">
              {/* Page Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Contract Management</h1>
                <p className="text-gray-600 mt-2">Manage staff contracts and documents</p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Contracts */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Contracts</p>
                      <p className="text-2xl font-bold text-gray-900">{contracts.length}</p>
                    </div>
                  </div>
                </div>

                {/* Active Contracts */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Contracts</p>
                      <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                    </div>
                  </div>
                </div>

                {/* Expired Contracts */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 bg-red-100 rounded-lg">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Expired Contracts</p>
                      <p className="text-2xl font-bold text-gray-900">{expiredCount}</p>
                    </div>
                  </div>
                </div>

                {/* Expiring Soon */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
                      <p className="text-2xl font-bold text-gray-900">{expiringSoonCount}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Within 60 days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contracts Table */}
              {loading ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading contracts...</p>
                    <p className="text-gray-500 text-sm mt-1">Please wait while we fetch your data</p>
                  </div>
                </div>
              ) : (
                <ContractsTable 
                  contracts={contracts} 
                  onUploadClick={handleUploadClick}
                  onViewClick={handleViewClick}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Modals */}
      {isUploadModalOpen && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSelectedStaffId(null);
          }}
          staffId={staffId}
          entityId={selectedStaffId}
          entityType="Staff"
          onUploadSuccessWithFiles={handleUploadSuccess}
          category="Contract Documents"
        />
      )}

      {isViewModalOpen && (
        <ViewContractModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedContract(null);
          }}
          contract={selectedContract}
        />
      )}
    </>
  );
}